import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { useToast } from '../components/ui/use-toast';
import CreateUserModal from '../components/CreateUserModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ISOProfile = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const [usersRes, dealsRes] = await Promise.all([
        axios.get(`${API_BASE}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/deals?type=all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data || []);
      setDeals(dealsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'User deleted successfully' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete user', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>

      {/* User Management */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">User Management</h2>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowCreateUser(true)}>
            <Plus size={16} className="mr-2" />
            Create New User
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Role</th>
              <th className="text-left py-3 px-4">Company</th>
              <th className="text-left py-3 px-4">Created</th>
              <th className="text-left py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-4 text-gray-500">No users found</td></tr>
            ) : (
              users.map((user, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">{user.name}</td>
                  <td className="py-3 px-4">{user.email}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                      {user.role?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">{user.company}</td>
                  <td className="py-3 px-4">{new Date(user.created_at || Date.now()).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button className="text-blue-600 hover:text-blue-800">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => deleteUser(user.id || user._id)} className="text-red-600 hover:text-red-800">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* All Deals */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-4">All Deals</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left py-3 px-4">#</th>
              <th className="text-left py-3 px-4">Date</th>
              <th className="text-left py-3 px-4">Business</th>
              <th className="text-left py-3 px-4">ISO</th>
              <th className="text-left py-3 px-4">Advanced</th>
              <th className="text-left py-3 px-4">Payback</th>
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Collected</th>
              <th className="text-left py-3 px-4">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {deals.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-4 text-gray-500">No deals found</td></tr>
            ) : (
              deals.map((deal, idx) => (
                <tr key={idx} className="border-b hover:bg-orange-50 cursor-pointer">
                  <td className="py-3 px-4 text-blue-600 font-medium">{deal.id}</td>
                  <td className="py-3 px-4">{deal.date}</td>
                  <td className="py-3 px-4">{deal.business}</td>
                  <td className="py-3 px-4">{deal.iso}</td>
                  <td className="py-3 px-4">${deal.advanced?.toLocaleString()}</td>
                  <td className="py-3 px-4">${deal.payback?.toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      deal.status === 'New' ? 'bg-blue-100 text-blue-700' :
                      deal.paid_status === 'Current' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {deal.status || deal.paid_status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-green-600">${deal.collected?.toLocaleString()}</td>
                  <td className="py-3 px-4">${deal.remaining?.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreateUser && <CreateUserModal onClose={() => { setShowCreateUser(false); loadData(); }} />}
    </div>
  );
};

export default ISOProfile;